import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatHotelDate } from '../../utils/dateUtils';
import { Groups as GroupsIcon, Login, Logout, MeetingRoom, NightsStay } from '@mui/icons-material';
import { buildBookingRoomChips } from '../../utils/bookingRoomChips';
import {
  formatBookingCurrency,
  getGuestInitials,
  getPaymentStatusColor,
  getPaymentStatusLabel,
  getUniqueRoomTypes,
  resolveBookingPaymentDisplay,
  resolveBookingStatus,
  getPartialStayNotice,
} from '../../utils/bookingOpsCardHelpers';
import { getNoShowOtaState } from '../../utils/noShowOtaStatus';
import {
  getBookingOpsCardColors,
  getBookingOpsClassicMetaDotSx,
  getBookingOpsClassicNoticeSx,
  getBookingOpsClassicPaymentRowSx,
  getBookingOpsClassicStayRowSx,
  getBookingOpsCodeTextSx,
  getBookingOpsDueChipSx,
  getBookingOpsRoomLineSeparatorSx,
  getBookingOpsRoomLineSx,
  getBookingOpsRoomNumberSegmentSx,
  getBookingOpsRoomTypesTextSx,
  resolveBookingRoomLineItems,
} from '../../utils/bookingOpsCardLayout';
import { resolveRoomBookingChannelLogoSrc } from '../../utils/staticUtils';
import {
  getUltraRoomAvatarSx,
  getUltraRoomCardBodySx,
  getUltraRoomCardSx,
  getUltraRoomChannelBadgeSx,
  getUltraRoomGuestNameSx,
  getUltraRoomHeroSx,
  getUltraRoomStatusChipSx,
} from '../../utils/dashboardUltraGridLayout';

export default function BookingOpsBookingCard({
  booking,
  isDark,
  accent = '#4ce1b6',
  isMobile = false,
  onClick,
  getRoomChips = buildBookingRoomChips,
  getRoomsCount = (b) => b.rooms || 0,
  showPartialCheckoutNotice = true,
}) {
  const { statusValue, statusColor, statusLabel } = resolveBookingStatus(booking);
  const noShowOta = getNoShowOtaState(booking);
  const colors = getBookingOpsCardColors(statusColor, isDark, statusValue);
  const roomTypes = getUniqueRoomTypes(booking);
  const roomChips = getRoomChips(booking);
  const roomLineItems = resolveBookingRoomLineItems(booking, roomChips);
  const roomDisplayCount = roomLineItems.length;
  const hasManyRooms = roomDisplayCount > 3;
  const payment = resolveBookingPaymentDisplay(booking);
  const paymentColor = getPaymentStatusColor(payment.paymentStatus);
  const isDue = payment.isDue;
  const showPartial =
    showPartialCheckoutNotice &&
    ['partial_checkin', 'partial_checkout'].includes(statusValue);
  const partialNotice = showPartial ? getPartialStayNotice(statusValue) : null;
  const checkInLabel = booking.check_in_date
    ? formatHotelDate(booking.check_in_date)
    : '—';
  const checkOutLabel = booking.check_out_date
    ? formatHotelDate(booking.check_out_date)
    : '—';
  const channelLogoSrc = resolveRoomBookingChannelLogoSrc(booking);
  const isGroupMember = Boolean(
    booking?.group_booking_id
    || booking?.group_booking_code
    || booking?.is_group_member
  );

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...getUltraRoomCardSx(isDark, isMobile, colors, { clickable: true }),
        width: '100%',
        height: hasManyRooms ? 'auto' : '100%',
      }}
    >
      <Box sx={{ ...getUltraRoomHeroSx(isMobile, isDark, colors), flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={statusLabel} sx={getUltraRoomStatusChipSx(statusColor, isDark)} />
          {noShowOta.otaSuccess ? (
            <Chip
              size="small"
              label={`${noShowOta.partnerLabel} OK`}
              sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 700,
                backgroundColor: isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(76, 175, 80, 0.14)',
                color: isDark ? '#81c784' : '#2e7d32',
              }}
            />
          ) : null}
          {noShowOta.needsRetry ? (
            <Chip
              size="small"
              label={`${noShowOta.partnerLabel} failed`}
              sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 700,
                backgroundColor: isDark ? 'rgba(244, 67, 54, 0.22)' : 'rgba(244, 67, 54, 0.12)',
                color: isDark ? '#ef9a9a' : '#c62828',
              }}
            />
          ) : null}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.4}
          sx={{
            ...getBookingOpsCodeTextSx(accent, isDark),
            display: 'inline-flex',
            maxWidth: '52%',
          }}
          title={
            isGroupMember
              ? (booking.group_booking_code ? `Group ${booking.group_booking_code}` : 'Group booking')
              : undefined
          }
        >
          {isGroupMember ? (
            <GroupsIcon
              sx={{
                fontSize: 14,
                color: accent,
                flexShrink: 0,
              }}
            />
          ) : null}
          <Typography component="span" sx={{ font: 'inherit', color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {booking.code}
          </Typography>
        </Stack>

        <Box sx={{ position: 'relative', zIndex: 1, mt: isMobile ? 0.35 : 0.45 }}>
          <Avatar sx={getUltraRoomAvatarSx(isMobile, isDark, colors)}>
            {getGuestInitials(booking.guest_name)}
          </Avatar>
          {isGroupMember ? (
            <Box
              title={booking.group_booking_code ? `Group ${booking.group_booking_code}` : 'Group booking'}
              sx={{
                position: 'absolute',
                left: -1,
                bottom: -1,
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? 18 : 20,
                height: isMobile ? 18 : 20,
                borderRadius: '50%',
                bgcolor: isDark ? '#0f172a' : '#ffffff',
                border: `1.5px solid ${alpha(accent, 0.85)}`,
                boxShadow: isDark
                  ? '0 1px 4px rgba(0,0,0,0.45)'
                  : '0 1px 4px rgba(15,23,42,0.18)',
                pointerEvents: 'none',
              }}
            >
              <GroupsIcon sx={{ fontSize: isMobile ? 11 : 12, color: accent }} />
            </Box>
          ) : null}
          {channelLogoSrc ? (
            <Box sx={getUltraRoomChannelBadgeSx(isMobile)}>
              <Box
                component="img"
                src={channelLogoSrc}
                alt={booking.channel_data?.name || booking.channel_name || 'Booking source'}
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          ) : null}
        </Box>

        <Typography
          noWrap
          sx={{
            ...getUltraRoomGuestNameSx(isDark, colors, false, isMobile),
            mt: isMobile ? 0.45 : 0.55,
            px: 0.5,
            maxWidth: '100%',
          }}
        >
          {booking.guest_name}
        </Typography>

        {roomLineItems.length > 0 ? (
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="center"
            spacing={0}
            sx={getBookingOpsRoomLineSx(isMobile, roomDisplayCount)}
          >
            {roomLineItems.map((item, index) => (
              <React.Fragment key={item.key || `${booking.id}-room-${index}`}>
                {index > 0 ? (
                  <Box component="span" sx={getBookingOpsRoomLineSeparatorSx(isDark, colors, roomDisplayCount)}>
                    ·
                  </Box>
                ) : null}
                <Typography
                  component="span"
                  sx={getBookingOpsRoomNumberSegmentSx(
                    isMobile,
                    isDark,
                    colors,
                    roomDisplayCount,
                    item.type === 'link',
                  )}
                >
                  {item.label}
                </Typography>
              </React.Fragment>
            ))}
          </Stack>
        ) : null}

        {roomTypes.length > 0 ? (
          <Typography sx={getBookingOpsRoomTypesTextSx(isDark)}>
            {roomTypes.join(' · ')}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ ...getUltraRoomCardBodySx(isDark, isMobile), ...(hasManyRooms ? {} : { flex: 1, minHeight: 0 }) }}>
        {!hasManyRooms ? <Box sx={{ flex: 1, minHeight: 0 }} /> : null}

        {partialNotice ? (
          <Typography variant="caption" sx={getBookingOpsClassicNoticeSx(isDark)}>
            {partialNotice}
          </Typography>
        ) : null}

        <Box sx={getBookingOpsClassicStayRowSx(isDark)}>
          <Stack direction="row" alignItems="center" spacing={0.35}>
            <Login sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
            <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
              {checkInLabel}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.35}>
            <Logout sx={{ fontSize: 14, color: '#ff7043', flexShrink: 0 }} />
            <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
              {checkOutLabel}
            </Typography>
          </Stack>
          <Typography component="span" variant="caption" sx={getBookingOpsClassicMetaDotSx(isDark)}>
            ·
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.35}>
            <NightsStay sx={{ fontSize: 14, color: '#90caf9', flexShrink: 0 }} />
            <Typography component="span" variant="caption">
              {booking.nights || 0} night{(booking.nights || 0) === 1 ? '' : 's'}
            </Typography>
          </Stack>
          <Typography component="span" variant="caption" sx={getBookingOpsClassicMetaDotSx(isDark)}>
            ·
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.35}>
            <MeetingRoom sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
            <Typography component="span" variant="caption">
              {getRoomsCount(booking)} room{getRoomsCount(booking) === 1 ? '' : 's'}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ ...getBookingOpsClassicPaymentRowSx(isDark, isDue), flexDirection: 'column', alignItems: 'stretch', gap: 0.35 }}>
          {payment.hasRestaurant ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ minWidth: 0, flexWrap: 'nowrap', overflow: 'hidden' }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? '#cbd5e1' : '#334155', fontWeight: 700, fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                  >
                    <Box component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, fontSize: '0.58rem', mr: 0.35 }}>
                      H
                    </Box>
                    {formatBookingCurrency(payment.hotel?.totalAmount, payment.currency)}
                    {payment.hotel?.isDue ? (
                      <Box component="span" sx={{ color: '#ff7043', fontWeight: 700, ml: 0.35, fontSize: '0.58rem' }}>
                        due
                      </Box>
                    ) : null}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? alpha('#fff', 0.25) : alpha('#000', 0.25) }}>
                    ·
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? '#cbd5e1' : '#334155', fontWeight: 700, fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                  >
                    <Box component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, fontSize: '0.58rem', mr: 0.35 }}>
                      R
                    </Box>
                    {formatBookingCurrency(payment.restaurant?.totalAmount, payment.currency)}
                    {payment.restaurant?.isDue ? (
                      <Box component="span" sx={{ color: '#ff7043', fontWeight: 700, ml: 0.35, fontSize: '0.58rem' }}>
                        due
                      </Box>
                    ) : null}
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
                  {payment.combined?.isDue ? (
                    <Chip
                      size="small"
                      label={getPaymentStatusLabel(payment.combined.paymentStatus)}
                      sx={getBookingOpsDueChipSx(isDark)}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        color: getPaymentStatusColor(payment.combined?.paymentStatus),
                        fontWeight: 700,
                        fontSize: '0.68rem',
                      }}
                    >
                      {getPaymentStatusLabel(payment.combined?.paymentStatus)}
                    </Typography>
                  )}
                  {payment.combined?.showDueBreakdown ? (
                    <Typography variant="caption" sx={{ color: '#ff7043', fontWeight: 700, fontSize: '0.62rem' }}>
                      {formatBookingCurrency(payment.combined.remainingAmount, payment.currency)} due
                    </Typography>
                  ) : null}
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: -0.2,
                    color: isDark ? '#f8fafc' : '#0f172a',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatBookingCurrency(payment.combined?.totalAmount, payment.currency)}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
                {isDue ? (
                  <Chip
                    size="small"
                    label={getPaymentStatusLabel(payment.paymentStatus)}
                    sx={getBookingOpsDueChipSx(isDark)}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: paymentColor, fontWeight: 700, fontSize: '0.68rem' }}
                  >
                    {getPaymentStatusLabel(payment.paymentStatus)}
                  </Typography>
                )}
                {payment.showDueBreakdown ? (
                  <Typography variant="caption" sx={{ color: '#ff7043', fontWeight: 700, fontSize: '0.65rem' }}>
                    {formatBookingCurrency(payment.remainingAmount, payment.currency)} due
                  </Typography>
                ) : null}
                {payment.showPaidBreakdown ? (
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.65rem' }}>
                    Paid {formatBookingCurrency(payment.totalPaid, payment.currency)}
                  </Typography>
                ) : null}
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.2,
                  color: isDark ? '#f8fafc' : '#0f172a',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatBookingCurrency(payment.totalAmount, payment.currency)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
